import { useState } from "react";

import { useFormStore } from "@/store/form";

// Utils
import { ErrorResponse, GenericObject } from "@/utils/types";
import useTabsValidation from "@/hooks/useTabsValidation";

// Components
import Layout from "@/components/Layout";

import Button from "@/components/Button";
import Columns from "@/components/Columns";
import Column from "@/components/Column";
import Form from "@/components/Form";
import PageHeader from "@/components/PageHeader";

// Icons
import AddressInfoFields, {
  addressInfoSchema,
} from "@/components/AddressInfoFields";
import { useProductStore } from "@/store/product";
import {
  removeAccentAndSpaceAndSetLowerCase,
  removeAccentsAndSetUpperCase,
} from "@/utils/string";
import { sendStepUserJourney } from "@/api/userJourney";
import { useActiveStore } from "@/store/activePages";
import { getOnlyNumbers } from "@/utils";
import usePageSubmit from "@/hooks/usePageSubmit";
import SEO from "@/components/SEO";
import { formatPrice } from "@/utils/currency";
import { useTagManager } from "@/hooks/useTagManager";

// Page Config
const pageConfig = {
  route: "/address-info",
  prevRoute: "/type-product",
  nextRoute: "/payment-data",
  notAvailableRoute: "/not-available",
};

// Address Info Data
export const AddressInfoPage = () => {
  const { dataLayer } = useTagManager();
  const [error, setError] = useState<Error>();
  const [formIsValid, setFormIsValid] = useState(false);

  const form = useFormStore((store) => store.value);
  const removePage = useActiveStore((store) => store.removePage);

  const [showContinueButton, setShowContinueButton] = useState(
    !!form.address.neighborhood
  );
  const product = useProductStore((store) => store.item);
  const currentPrice = useProductStore((store) =>
    store.getCurrentPrice(getOnlyNumbers(form.tel).slice(0, 2))
  );
  const totalGbProduct = useProductStore((store) => store.getTotalGbProduct());

  const submitCallback = usePageSubmit(pageConfig.nextRoute);

  const handleOnSubmit = async (formData: GenericObject) => {
    try {
      dataLayer({
        event: "step",
        passo: "ENDERECO",
        sku: removeAccentsAndSetUpperCase(product?.tim_id),
        nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
          product?.nome
        )}-${totalGbProduct}`,
        valor: formatPrice(currentPrice),
        status: "Sucesso",
      });
      // No need to set status again on success
      submitCallback(formData);
    } catch (err: unknown) {
      dataLayer({
        event: "step",
        passo: "ENDERECO",
        sku: removeAccentsAndSetUpperCase(product?.tim_id),
        nome_produto: `${removeAccentAndSpaceAndSetLowerCase(
          product?.nome
        )}-${totalGbProduct}`,
        valor: formatPrice(currentPrice),
        status: "Erro",
      });
      if ((err as ErrorResponse).errors) {
        setError({
          message: (err as ErrorResponse).errors[0].description,
        } as Error);
      } else {
        setError(err as Error);
      }
    }
  };

  useTabsValidation(pageConfig.route, pageConfig.prevRoute);

  const onClickForwardButton = () => {
    sendStepUserJourney({
      passo: "ENDERECO",
      uuid: form.uuid,
    });
  };

  return (
    <Layout>
      <SEO
        title="Dados Pessoais - Complemento"
        description="Adicione os dados pessoais do titular da conta para continuar comprando seu plano TIM!"
      />

      <Column size={12}>
        <PageHeader
          title="Certo, preencha o CEP abaixo que tentaremos buscar sua rua e bairro automaticamente."
          dataIcTitle="sec-omega-comunicacao"
        />
      </Column>

      <Columns
        as={Form}
        onSubmit={handleOnSubmit}
        validationSchema={addressInfoSchema}
        className="pt-6"
        justifyContent="center"
        alignItems="start"
      >
        {/* Fields */}
        <AddressInfoFields
          setFormIsValid={setFormIsValid}
          setShowContinueButton={setShowContinueButton}
        />

        {/* Error Handler */}
        {error && (
          <Column size={12} className="pb-4">
            <div className="flex w-full items-center rounded-md border-2 border-red-300 bg-red-200 px-3 py-2 text-red-600">
              <img
                src="/alert.svg"
                alt="Ícone de alerta"
                className="mr-2 h-5 w-5 opacity-75"
              />
              {error.message}
            </div>
          </Column>
        )}

        {showContinueButton && (
          <Column size={12}>
            <Button
              type="submit"
              large
              disabled={!formIsValid}
              helperText={
                !formIsValid ? "todos os campos são obrigatórios" : ""
              }
              onClick={() => onClickForwardButton()}
            >
              {!formIsValid ? "Preencha os campos para continuar" : "Continuar"}
            </Button>
          </Column>
        )}
        <Column size={12} className="mt-8">
          <Button
            type="button"
            kind="secondary"
            large
            onClick={() => removePage(pageConfig.route)}
            to={pageConfig.prevRoute}
            data-ic-trigger="sec-omega-voltar"
          >
            Voltar
          </Button>
        </Column>
      </Columns>
    </Layout>
  );
};

export default AddressInfoPage;
